import React, { useRef, useState, useEffect } from "react";
import { AttachmentSharp, Send } from "@mui/icons-material";
import axios from "axios";

const ChatInput = ({
  conversations,
  selectedApplicationId,
  handleSendReply,
  senderEmail,
}) => {
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [fileName, setFileName] = useState(""); // Current file name for display
  const [attachmentFile, setAttachmentFile] = useState(null); // Store file object
  const [previewUrl, setPreviewUrl] = useState(null);

  const incomingUsersList = conversations?.history?.users?.slice(1) || []; // skip first user
  const anotherList = [
    {
      email_id: "aryay@nativeorange.ai",
      description: "Arya",
      signature: null,
    },
    {
      email_id: "prakash@nativeorange.ai",
      description: "Prakash",
      signature: null,
    },
    {
      email_id: "TMcMillan@central-insurance.com",
      description: "Thomas",
      signature: null,
    },
  ];

  const usersList = [...incomingUsersList, ...anotherList];

  // Adjust the textarea height dynamically
  const adjustTextAreaHeight = () => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto"; // Reset height to auto to shrink
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`; // Set height to scrollHeight to expand
    }
  };

  useEffect(() => {
    adjustTextAreaHeight(); // Adjust height when inputValue changes
  }, [inputValue]);

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name); // Save the file name to the state
      setAttachmentFile(file); // Save the file object for FormData
      setPreviewUrl(URL.createObjectURL(file));
      // You can upload the file or preview it here
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const previousValue = inputValue; // previous state before typing
    setInputValue(value);
    // Check if the newly typed character is "@"
    if (
      value.length > previousValue.length &&
      value[value.length - 1] === "@"
    ) {
      setFilteredUsers(usersList);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  function extractMentionedEmails(text, userList) {
    // Capture mentions after @ (allow one or two words, handle punctuation)
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const mention = match[1].trim();
      mentions.push(mention);
    }

    // Match mentions to user descriptions
    const emails = mentions
      .map((mention) => {
        const lowerMention = mention.toLowerCase();
        const user = userList.find(
          (user) =>
            user.description &&
            user.description.toLowerCase().startsWith(lowerMention)
        );
        if (!user) console.warn(`No match for mention: @${mention}`);
        return user ? user.email_id : null;
      })
      .filter(Boolean);

    return emails;
  }

  const handleMentionSelect = (user) => {
    // Replace @ with selected user name in input
    const updatedInput = inputValue.replace(/@$/, `@${user.description} `);
    setInputValue(updatedInput);
    // Hide mention dropdown
    setShowMentions(false);
  };

  // Updated handleReply function using multipart/form-data
  const handleReply = async () => {
    const receiverEmail = extractMentionedEmails(inputValue, usersList);

    const finalMessage = await handleSendReply(
      inputValue,
      senderEmail,
      receiverEmail,
      previewUrl
    );
    if (!finalMessage) {
      return;
    }

    const headers = {
      "X-Access-Token":
        "bjZDbAhu9GabUZZqqk64FbaXK9GcTErIeDu5sVc6Ryt2USIgbwQUmZdvUXSevxyF",
      "X-Refresh-Token":
        "f68LK2Y5Py5ErV0qz81j4wGo9iIH7S1Aw5lLos7wDUPyitYmTSQ1Lut8s9AVYYys",
      Accept: "application/json",
      // Do not explicitly set Content-Type; axios will set it for FormData.
    };

    const formData = new FormData();
    formData.append("application_id", selectedApplicationId);
    formData.append("to_emails", receiverEmail);
    formData.append("comment", finalMessage); // ✅ use rewritten and confirmed
    if (attachmentFile) {
      formData.append("attachments", attachmentFile);
    }
    formData.append("to_emails_cc", "");
    formData.append("to_emails_bcc", "");

    if (attachmentFile) {
      try {
        const response = await axios.post(
          "https://insureai-communicator-1074099157700.us-central1.run.app/api/v1/conversations/reply",
          formData,
          { headers }
        );
        setInputValue("");
        setFileName("");
        setAttachmentFile(null);
      } catch (error) {
        console.error("Error sending reply:", error);
      }
    } else {
      try {
        const response = await axios.post(
          "https://insureai-communicator-1074099157700.us-central1.run.app/api/v1/conversations/reply_without_attachments",
          formData,
          { headers }
        );
        setInputValue("");
        setFileName("");
        setAttachmentFile(null);
      } catch (error) {
        console.error("Error sending reply:", error);
      }
    }
  };

  return (
    <div className="relative m-3 flex flex-col space-y-2 bg-[#f1f2f7]">
      {/* Input and Attachment Row */}
      <div className="w-full flex items-center space-x-2 bg-[#f1f2f7]">
        {/* Textarea for auto-resizable input */}
        <div className="w-full bg-white px-2 flex items-center bg-[#f1f2f7] rounded-full border-t relative">
          <textarea
            ref={textAreaRef}
            placeholder="Send your text"
            className="flex-1 px-4 py-2 rounded-full  outline-none resize-none"
            value={inputValue}
            onChange={handleInputChange}
            rows={1} // Start with 1 row, textarea will expand
          />

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          <AttachmentSharp
            className="text-gray-500 cursor-pointer"
            onClick={handleAttachmentClick}
          />
        </div>

        {/* Send button (on the same row as the input and attachment) */}
        <div className="flex items-center justify-between">
          <button
            className="bg-[#f26522] p-2 rounded-full flex items-center justify-center"
            onClick={handleReply}
          >
            <Send className="text-white" fontSize="10px" />
          </button>
        </div>
      </div>

      {/* Mention dropdown */}
      {showMentions && (
        <div className="absolute bottom-12 left-4 bg-white shadow-md rounded-md max-h-40 overflow-y-auto w-60 z-50">
          {filteredUsers.map((user, index) => (
            <div
              key={index}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer  text-black"
              onClick={() => handleMentionSelect(user)}
            >
              {user.description}
            </div>
          ))}
        </div>
      )}

      {/* Display file name below the input */}
      {fileName && (
        <div className=" text-gray-600 mt-2">
          <span>Attached: </span>
          {fileName}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
